import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { getGreeting } from "@/lib/greeting";

const SESSION_KEY = "evermin_welcomed";

// Subtle, auto-dismissing personal welcome for logged-in users. Shows once per
// browser session on the homepage. Computed from the user's LOCAL time; the
// name comes only from their saved profile (never from their email).
export function WelcomeBanner() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  const greeting = useMemo(
    () => (user ? getGreeting(user.name, language) : null),
    // Recompute only when the user identity changes, so it stays stable while shown.
    [user, language]
  );

  useEffect(() => {
    if (!user || pathname !== "/") return;
    let shown = false;
    try { shown = sessionStorage.getItem(SESSION_KEY) === "1"; } catch (e) {}
    if (shown) return;
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) {}
    setVisible(true);
    const hide = setTimeout(() => setVisible(false), 6000);
    return () => clearTimeout(hide);
  }, [user, pathname]);

  if (!visible || !greeting) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-50 flex justify-center px-4" data-testid="welcome-banner">
      <div className="animate-in slide-in-from-top-2 fade-in pointer-events-auto flex max-w-md items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-3 shadow-premium backdrop-blur-xl duration-500">
        <span className="text-2xl leading-none" aria-hidden="true">{greeting.icon}</span>
        <div className="min-w-0">
          <p className="font-display truncate text-sm font-bold text-slate-900" data-testid="welcome-line">{greeting.line}</p>
          <p className="truncate text-xs text-slate-500">{greeting.sub}</p>
        </div>
        <button
          onClick={() => setVisible(false)}
          data-testid="welcome-dismiss"
          aria-label="Dismiss"
          className="ml-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
